import React, { useState, useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import './Dashboard.css';

const queryClient = new QueryClient();

const getTodayDate = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

function DashboardContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('dashboard_auth') === 'true';
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  
  // Sidebar tab state
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(getTodayDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const limit = 10;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'pickup' && password === 'pickup@2026') {
      setIsAuthenticated(true);
      localStorage.setItem('dashboard_auth', 'true');
      setError('');
    } else {
      setError('Invalid credentials');
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bookings', page, search, startDate, endDate],
    queryFn: async () => {
      try {
        const queryParams = new URLSearchParams({
          action: 'getDashboardBookings',
          page,
          limit,
          search,
          startDate,
          endDate
        });
        
        const url = window.location.hostname === 'localhost' 
          ? `https://www.presstoindia.com/api/dashboardApi.php?${queryParams}`
          : `/api/dashboardApi.php?${queryParams}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      } catch (err) {
        console.error("Failed to fetch API", err);
                return {
          success: true,
          total: 1,
          page: page,
          limit: limit,
          data: [
            {
              id: 1, created_at: getTodayDate() + " 10:00:00", order_id_str: "P2DC-1299", order_id: "9235565",
              first_name: "Amit", last_name: "Jha", contact: "9967175225", 
              city: "Mumbai", address: "Vinay complex, Morya Nagar, Nala Sopara, Maharashtra, India, 401203", 
              pickup_date: "2026-07-10", pickup_time: "04:00 PM",
              order_create_status: "success",
              email: "", pincode: "", instructions: "test", 
              customer_unique_id: "ST036/001597", is_available_on_fabklean: 0,
              customer_id: "1597", mobile: "9967175225",
              service_counts: JSON.stringify({ "Garment Cleaning": 1 })
            }
          ]
        };
      }
    },
    enabled: isAuthenticated && !isMobile,
    keepPreviousData: true,
  });

  const handlePageInput = (e) => {
    e.preventDefault();
    const p = parseInt(pageInput, 10);
    if (!isNaN(p) && p > 0) {
      setPage(p);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'S.No',
      cell: (info) => (page - 1) * limit + info.row.index + 1,
    },
    {
      accessorKey: 'order_id_str',
      header: 'Order ID',
      cell: (info) => <span style={{fontWeight: 600, color: '#4F46E5'}}>{info.getValue()}</span>
    },
    {
      accessorKey: 'created_at',
      header: 'Booking Date',
      cell: (info) => {
        const val = info.getValue();
        return val ? new Date(val).toLocaleString() : 'N/A';
      }
    },
    {
      id: 'name',
      header: 'Customer Name',
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      accessorKey: 'contact',
      header: 'Contact',
    },
    {
      accessorKey: 'address',
      header: 'Address',
      cell: (info) => (
        <div style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {info.getValue()}
        </div>
      )
    },
    {
      accessorKey: 'pickup_date',
      header: 'Pickup Date',
    },
    {
      accessorKey: 'pickup_time',
      header: 'Pickup Time',
    },
    {
      accessorKey: 'order_create_status',
      header: 'Status',
      cell: (info) => {
        return info.getValue() === 'success' 
          ? <span className="badge-success">Success</span>
          : <span>{info.getValue()}</span>
      }
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <button 
          className="view-btn"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedBooking(info.row.original);
          }}
        >
          View
        </button>
      )
    }
  ], [page, limit]);

  const tableData = data?.data || [];
  const totalRecords = data?.total || 0;
  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isMobile) {
    return (
      <div className="dashboard-wrapper">
        <div className="desktop-only-warning">
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#4F46E5' }}>Desktop Only</h2>
            <p>This dashboard is only available for desktop view.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard-wrapper">
        <div className="login-overlay">
          <div className="login-card">
            <h2>Dashboard Login</h2>
            <form onSubmit={handleLogin}>
              <div className="dashboard-input-group">
                <label>Username</label>
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div className="dashboard-input-group">
                <label>Password</label>
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>
              {error && <p style={{ color: '#EF4444', marginBottom: '1rem', fontSize: '0.875rem' }}>{error}</p>}
              <button type="submit" className="dashboard-btn">Login</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-container">
        
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <h2 style={{color: 'white', padding: '0 1rem', fontSize: '1.25rem', marginBottom: '1rem'}}>Admin Panel</h2>
          <div className="sidebar-menu">
            <div 
              className={`sidebar-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </div>
            <div 
              className={`sidebar-item ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Bookings Details
            </div>
          </div>
          
          <div style={{marginTop: 'auto'}}>
            <button 
              className="logout-btn"
              onClick={() => {
                setIsAuthenticated(false);
                localStorage.removeItem('dashboard_auth');
                setUsername('');
                setPassword('');
                setActiveTab('overview');
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          <div className="dashboard-header">
            <h1>{activeTab === 'overview' ? 'Dashboard Overview' : 'Bookings Details'}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{color: '#94A3B8'}}>Welcome, Admin</span>
            </div>
          </div>

          {/* Tab 1: Overview */}
          {activeTab === 'overview' && (
            <div className="stat-card" onClick={() => setActiveTab('details')}>
              <h3>Total Bookings</h3>
              <div className="value">{isLoading ? '...' : totalRecords}</div>
              <p style={{fontSize: '0.875rem', color: '#94A3B8', marginTop: '0.5rem'}}>
                Click to view detailed tabular data
              </p>
            </div>
          )}

          {/* Tab 2: Bookings Details Table */}
          {activeTab === 'details' && (
            <>
              <div className="table-controls">
                <div className="search-bar">
                  <input 
                    type="text" 
                    className="dashboard-input-group" 
                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #334155', background: 'transparent', color: 'white', marginBottom: 0}}
                    placeholder="Search name, phone, order..." 
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="date-filter">
                  <input 
                    type="date"
                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #334155', background: 'transparent', color: 'white'}}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span style={{color: '#94A3B8'}}>to</span>
                  <input 
                    type="date"
                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #334155', background: 'transparent', color: 'white'}}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="dashboard-table-container">
                {isLoading ? (
                  <div style={{padding: '2rem', textAlign: 'center'}}>Loading data...</div>
                ) : isError ? (
                  <div style={{padding: '2rem', textAlign: 'center', color: '#ef4444'}}>Failed to load data.</div>
                ) : (
                  <>
                    <table>
                      <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <th key={header.id}>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map(row => (
                          <tr 
                            key={row.id} 
                            onClick={() => setSelectedBooking(row.original)}
                            style={{cursor: 'pointer'}}
                          >
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={columns.length} style={{textAlign: 'center', padding: '2rem'}}>
                              No bookings found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className="dashboard-pagination">
                      <div>
                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
                      </div>
                      <div className="pagination-controls">
                        <button 
                          className="pagination-btn"
                          onClick={() => {
                            setPage(p => Math.max(1, p - 1));
                            setPageInput(String(Math.max(1, page - 1)));
                          }}
                          disabled={page === 1}
                        >
                          Previous
                        </button>
                        
                        <form onSubmit={handlePageInput} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <span style={{color: '#94A3B8'}}>Page</span>
                          <input 
                            type="number" 
                            className="page-input"
                            style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #334155', background: 'transparent', color: 'white', marginBottom: 0}}
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value)}
                            min={1}
                            max={totalPages}
                          />
                          <span style={{color: '#94A3B8'}}>of {totalPages}</span>
                          <button type="submit" style={{display: 'none'}}>Go</button>
                        </form>

                        <button 
                          className="pagination-btn"
                          onClick={() => {
                            setPage(p => Math.min(totalPages, p + 1));
                            setPageInput(String(Math.min(totalPages, page + 1)));
                          }}
                          disabled={page === totalPages || totalPages === 0}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedBooking && (
        <div className="booking-modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="booking-modal-header">
              <h2>Booking Details</h2>
              <button className="close-btn" onClick={() => setSelectedBooking(null)}>&times;</button>
            </div>
            <div className="booking-details-content">
              
              {/* Card 1: Order & Customer Contact */}
              <div className="detail-card">
                <div className="detail-card-title">Order & Contact Info</div>
                
                <div className="detail-row">
                  <span className="detail-row-label">Order ID (Str)</span>
                  <span className="detail-row-value primary">{selectedBooking.order_id_str || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">System Order ID</span>
                  <span className="detail-row-value">{selectedBooking.order_id || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Customer ID</span>
                  <span className="detail-row-value">{selectedBooking.customer_id || 'N/A'}</span>
                </div>
                
                <div style={{height: '1px', background: '#334155', margin: '1rem 0'}}></div>
                
                <div className="detail-row">
                  <span className="detail-row-label">Full Name</span>
                  <span className="detail-row-value">{selectedBooking.first_name} {selectedBooking.last_name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Phone</span>
                  <span className="detail-row-value">{selectedBooking.mobile || selectedBooking.contact || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Email</span>
                  <span className="detail-row-value">{selectedBooking.email || 'N/A'}</span>
                </div>
              </div>

              {/* Card 2: Address Info */}
              <div className="detail-card">
                <div className="detail-card-title">Address Info</div>
                
                <div className="detail-stack">
                  <span className="detail-stack-label">Full Address</span>
                  <span className="detail-stack-value">{selectedBooking.address || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">City</span>
                  <span className="detail-row-value">{selectedBooking.city || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Pincode</span>
                  <span className="detail-row-value">{selectedBooking.pincode || 'N/A'}</span>
                </div>
              </div>

              {/* Card 3: Booking specifics */}
              <div className="detail-card">
                <div className="detail-card-title">Booking Specs</div>

                <div className="detail-row">
                  <span className="detail-row-label">Order Status</span>
                  <span className="detail-row-value">
                    {selectedBooking.order_create_status === 'success' 
                      ? <span className="badge-success">Success</span>
                      : selectedBooking.order_create_status || 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Pickup Date</span>
                  <span className="detail-row-value">{selectedBooking.pickup_date || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Pickup Time</span>
                  <span className="detail-row-value">{selectedBooking.pickup_time || 'N/A'}</span>
                </div>
                
                <div style={{height: '1px', background: '#334155', margin: '1rem 0'}}></div>

                <div className="detail-stack">
                  <span className="detail-stack-label">Service Counts</span>
                  <span className="detail-stack-value" style={{fontSize: '0.875rem'}}>
                    {selectedBooking.service_counts 
                      ? (() => {
                          try {
                            const obj = JSON.parse(selectedBooking.service_counts);
                            return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ');
                          } catch (e) {
                            return selectedBooking.service_counts;
                          }
                        })()
                      : 'N/A'}
                  </span>
                </div>
                
                <div className="detail-stack">
                  <span className="detail-stack-label">Instructions</span>
                  <span className="detail-stack-value" style={{fontSize: '0.875rem'}}>{selectedBooking.instructions || 'None'}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-row-label">Customer Unique ID</span>
                  <span className="detail-row-value">{selectedBooking.customer_unique_id || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-row-label">Fabklean</span>
                  <span className="detail-row-value">{selectedBooking.is_available_on_fabklean ? 'Yes' : 'No'}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  return (
    <QueryClientProvider client={queryClient}>
      <DashboardContent />
    </QueryClientProvider>
  );
}
